import React, { useEffect, useRef } from 'react';
import { inputBaseRootStyle } from '../../setup/theme/impl/input';
import { BpDesignSystem } from '../../types';
import { styled } from '../../styles';
import { isNumber, isUndefined } from '../../utils/guards';
import { Keys } from '../../utils/keysUtil';
import { Box } from '../Box/Box';
import { FormLabel } from '../Form/FormLabel';
import { Typography } from '../Typography/Typography';

type Props = BpDesignSystem.Props<{
  value: string;
  onChange(value: string): void;
  validOptions?: string[];
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  label?: string;
  id?: string;
  subLabel?: string;
  required?: boolean;
  resize?: boolean;
  preserveLineBreaks?: boolean;
}>;

const HIGHLIGHT_VALID_NAME = 'highlight-valid';
const HIGHLIGHT_ERROR_NAME = 'highlight-error';
const HIGHLIGHT_DEFAULT_NAME = 'highlight-default';

let highlightValid: Highlight | undefined;
let highlightError: Highlight | undefined;
let highlightDefault: Highlight | undefined;
if (CSS && CSS.highlights && 'set' in CSS.highlights && typeof CSS.highlights.set === 'function') {
  highlightValid = new Highlight();
  highlightError = new Highlight();
  highlightDefault = new Highlight();
  CSS.highlights.set(HIGHLIGHT_VALID_NAME, highlightValid);
  CSS.highlights.set(HIGHLIGHT_ERROR_NAME, highlightError);
  CSS.highlights.set(HIGHLIGHT_DEFAULT_NAME, highlightDefault);
}

export const MenuPopperInput: React.FC<Props> = ({
  value: inputValue,
  onChange,
  validOptions,
  label,
  required,
  subLabel,
  dataTest,
  id,
  className,
  style,
  sx,
  maxLength = 9999,
  resize,
  placeholder,
  rows,
  preserveLineBreaks
}) => {
  const ref = useRef<HTMLPreElement | null>(null);
  const isTextarea = !!rows;

  const value = maxLength ? inputValue.slice(0, maxLength) : inputValue;

  const [isEmpty, setIsEmpty] = React.useState(!value);
  const [isFocused, setIsFocused] = React.useState(false);

  useEffect(() => {
    colorize(ref.current, validOptions);
    setIsEmpty(!value);
  }, [value, validOptions]);

  return (
    <>
      {label && (
        <FormLabel focused={isFocused} htmlFor={id} required={required} className="menu-popper-input-label">
          {label}
        </FormLabel>
      )}
      {subLabel && (
        <Typography variant="label" small className="menu-popper-input-sub-label">
          {subLabel}
        </Typography>
      )}
      <StyledEditableArea
        dataTest={dataTest}
        style={style}
        sx={sx}
        id={id}
        className={className}
        $textarea={isTextarea}
        $placeholder={isEmpty ? placeholder : undefined}
        $resize={isTextarea && !!resize}
        $additionalHeight={!!rows ? (rows - 1) * 19 : undefined}
      >
        {/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: specific use case */}
        <pre
          ref={ref}
          contentEditable
          aria-placeholder={placeholder}
          suppressContentEditableWarning
          onFocus={() => {
            setIsFocused(true);
          }}
          onKeyDown={(e) => {
            const text = (e.target as HTMLPreElement).textContent;
            if (
              text?.length + 1 > (maxLength ?? 9999) &&
              !Keys.oneOf(e, 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown')
            ) {
              e.preventDefault();
              e.stopPropagation();
            }
            if (!isTextarea && Keys.oneOf(e, 'Enter')) {
              e.stopPropagation();
              e.preventDefault();
            }
            // workaround a bug in chrome where enter in a contenteditable pre inserts <div><br></div>
            if (isTextarea && Keys.oneOf(e, 'Enter')) {
              document.execCommand('insertLineBreak');
              e.preventDefault();
            }
            colorize(ref.current, validOptions);
          }}
          onInput={(e) => {
            const text = (e.target as HTMLPreElement).textContent;
            setIsEmpty(!text || text === '\n');
            if (text?.length >= (maxLength ?? 9999)) {
              e.preventDefault();
              e.preventDefault();
            }
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text/plain');
            if (!isTextarea) {
              // Paste as plain text and replace new lines with spaces
              const textNoNewLines = text?.replace(/\n/g, ' ');
              document.execCommand('insertText', false, textNoNewLines.substring(0, maxLength));
              e.preventDefault();
            } else {
              document.execCommand('insertText', false, text.substring(0, maxLength));
              e.preventDefault();
            }
          }}
          onBlur={(e) => {
            const target = e.target as HTMLPreElement;
            const text = preserveLineBreaks ? serializeValue(target) : target.textContent;
            setIsFocused(false);
            onChange(text !== '\n' ? text : '');
          }}
        >
          {value}
        </pre>
      </StyledEditableArea>
    </>
  );
};

const StyledEditableArea = styled(Box)<{
  $textarea: boolean;
  $resize: boolean;
  $placeholder?: string;
  $additionalHeight?: number;
}>(({ theme, $textarea, $placeholder, $resize, $additionalHeight }) => ({
  appearance: $textarea ? 'textarea' : 'textfield',
  ...inputBaseRootStyle,
  width: '100%',
  display: 'flex',
  position: 'relative',
  paddingBlock: theme.spacingSize.s,
  overflowClipMargin: '0px',
  overflowX: 'clip',
  overflowY: $textarea ? 'auto' : 'clip',
  container: 'editable-area / inline-size',
  '.menu-popper-input-label + &, .menu-popper-input-sub-label + &': {
    marginTop: theme.spacingSize.m
  },
  minHeight: $additionalHeight ? inputBaseRootStyle.minHeight + $additionalHeight : inputBaseRootStyle.minHeight,
  maxHeight: $additionalHeight ? inputBaseRootStyle.minHeight + $additionalHeight : inputBaseRootStyle.minHeight,
  resize: $resize ? 'both' : 'none',

  '& > pre': {
    outline: 'none',
    padding: '7px 4px 5px 4px',
    margin: 0,
    overflow: !$textarea ? 'auto' : undefined,
    whiteSpace: $textarea ? 'break-spaces' : undefined,
    scrollbarWidth: 'none',
    fontSize: inputBaseRootStyle.fontSize,
    fontFamily: inputBaseRootStyle.fontFamily,

    '&::before': {
      content: $placeholder ? `"${$placeholder}"` : '""',
      position: 'absolute',
      top: inputBaseRootStyle.paddingTop,
      left: inputBaseRootStyle.paddingLeft,
      color: '#808080',
      pointerEvents: 'none',
      userSelect: 'none',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'break-word',
      width: 'calc(100% - 8px)',
      display: 'block',
      visibility: $placeholder ? 'visible' : 'hidden'
    },

    [`::highlight(${HIGHLIGHT_VALID_NAME})`]: {
      backgroundColor: theme.tokens.colorSuccessContainer,
      color: theme.tokens.colorOnSuccessContainer
    },
    [`::highlight(${HIGHLIGHT_ERROR_NAME})`]: {
      backgroundColor: theme.tokens.colorAlertContainer,
      color: theme.tokens.colorOnAlertContainer
    },
    [`::highlight(${HIGHLIGHT_DEFAULT_NAME})`]: {
      backgroundColor: theme.tokens.colorSurfaceVariant
    }
  },

  '@container editable-area (width > 1px)': {
    '& > pre': {
      width: '100cqw'
    }
  }
}));

const serializeValue = (element: HTMLElement): string => {
  let result = '';
  element.childNodes.forEach((node) => {
    if (node.nodeName === 'BR') {
      result += '\n';
    } else if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent ?? '';
    } else if (node instanceof HTMLElement) {
      result += serializeValue(node);
    }
  });
  return result;
};

const colorize = (el?: HTMLPreElement | null, validOptions?: string[]) => {
  if (!CSS.highlights || !el || !el?.childNodes?.length) {
    return;
  }
  const textNodes = Array.from(el.childNodes);

  for (const textNode of textNodes) {
    const text = textNode.textContent || '';

    const leftBracketIndex: number[] = [];
    const rightBracketIndex: number[] = [];

    for (let i = 0; i < text.length; i++) {
      if (text[i] === '[') {
        leftBracketIndex.push(i);
      }
      if (text[i] === ']') {
        rightBracketIndex.push(i);
      }
    }

    for (let i = 0; i < leftBracketIndex.length; i++) {
      const currentLeftBracket = leftBracketIndex[i];

      if (isNumber(currentLeftBracket)) {
        const nextRightBracket = rightBracketIndex.find((it) => it > currentLeftBracket);
        if (isUndefined(nextRightBracket)) {
          return;
        }
        const range = new Range();
        range.setStart(textNode, currentLeftBracket);
        range.setEnd(textNode, nextRightBracket + 1);

        if (!validOptions?.length) {
          highlightDefault?.add(range);
        } else {
          // +1/-1 to exclude the brackets
          const content = text.substring(currentLeftBracket + 1, nextRightBracket);
          if (validOptions.includes(content)) {
            highlightValid?.add(range);
          } else {
            highlightError?.add(range);
          }
        }
      }
    }
  }
};
